import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import {
  PhoneOff,
  Mic,
  MicOff,
  Pause,
  Play,
  X,
  Grid3X3,
  ChevronDown,
  ArrowRightLeft,
  Check,
  Volume2,
  Volume,
} from 'lucide-react'
import { useCallStore, resumeTwilioAudio } from '@/stores/callStore'
import { usePermission } from '@/hooks/usePermission'
import { apiClient, warmCsrf } from '@/lib/api'
import { apiUrl } from '@/lib/apiUrl'
import { getCsrfToken } from '@/lib/csrfToken'
import { shallow } from 'zustand/shallow'
import { DialPad } from './DialPad'
import { SoftphonePanel } from './SoftphonePanel'
import { IncomingCallToast } from './IncomingCallToast'
import { MobileIncomingCallOverlay } from './MobileIncomingCallOverlay'
import { ScreenPop } from './ScreenPop'
import { QuietHoursModal } from './QuietHoursModal'
import { PhoneOfflineBanner } from './PhoneOfflineBanner'
import { MicPermissionBanner } from './MicPermissionBanner'
import { ConnectionQualityIndicator } from './ConnectionQualityIndicator'
import { PostCallCard } from './PostCallCard'
import { AvatarStateRing, type CallRingState } from './AvatarStateRing'
import { CallControlDock, CallControlDockButton } from './CallControlDock'
import { PhoneSheet } from './PhoneSheet'
import { useUserInteracted } from '@/hooks/useUserInteracted'
import { useUIStore } from '@/stores/uiStore'
import { useNavigate } from 'react-router-dom'
import type { MakeCallMeta } from '@/stores/callStore'
import { SafeAreaBottom } from '@/components/ui/SafeAreaBottom'

// ─── Call Timer ───────────────────────────────────────────────────────────────

function CallTimer({
  startTime,
  variant = 'compact',
}: {
  startTime: number | null
  variant?: 'compact' | 'hero'
}) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    if (!startTime) {
      setElapsed(0)
      return
    }
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [startTime])

  const mins = Math.floor(elapsed / 60)
  const secs = elapsed % 60
  const text = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`

  if (variant === 'hero') {
    return (
      <span
        className="date-hero"
        style={{ fontVariantNumeric: 'tabular-nums', color: '#FFFFFF', display: 'inline-block' }}
      >
        {text}
      </span>
    )
  }
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'rgba(255,255,255,0.60)' }}>
      {text}
    </span>
  )
}

// ─── Softphone Widget ────────────────────────────────────────────────────────

export function Softphone() {
  const {
    activeCall,
    callStatus,
    callDirection,
    isMuted,
    isOnHold,
    isSpeakerOn,
    callerInfo,
    incomingCall,
    callStartTime,
    dialPadOpen,
    softphoneExpanded,
    error,
    transferStatus,
    transferTarget,
    initDevice,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    holdCall,
    resumeCall,
    toggleSpeaker,
    makeCall,
    setDialPadOpen,
    setSoftphoneExpanded,
    clearError,
    initiateWarmTransfer,
    completeWarmTransfer,
    cancelWarmTransfer,
    resetTransfer,
    activeCallSid,
    connectionQuality,
  } = useCallStore(
    (s) => ({
      activeCall:             s.activeCall,
      callStatus:             s.callStatus,
      callDirection:          s.callDirection,
      isMuted:                s.isMuted,
      isOnHold:               s.isOnHold,
      isSpeakerOn:            s.isSpeakerOn,
      callerInfo:             s.callerInfo,
      incomingCall:           s.incomingCall,
      callStartTime:          s.callStartTime,
      dialPadOpen:            s.dialPadOpen,
      softphoneExpanded:      s.softphoneExpanded,
      error:                  s.error,
      transferStatus:         s.transferStatus,
      transferTarget:         s.transferTarget,
      connectionQuality:      s.connectionQuality,
      initDevice:             s.initDevice,
      acceptCall:             s.acceptCall,
      rejectCall:             s.rejectCall,
      hangUp:                 s.hangUp,
      toggleMute:             s.toggleMute,
      holdCall:               s.holdCall,
      resumeCall:             s.resumeCall,
      toggleSpeaker:          s.toggleSpeaker,
      makeCall:               s.makeCall,
      setDialPadOpen:         s.setDialPadOpen,
      setSoftphoneExpanded:   s.setSoftphoneExpanded,
      clearError:             s.clearError,
      initiateWarmTransfer:   s.initiateWarmTransfer,
      completeWarmTransfer:   s.completeWarmTransfer,
      cancelWarmTransfer:     s.cancelWarmTransfer,
      resetTransfer:          s.resetTransfer,
    activeCallSid:          s.activeCallSid,
    }),
    shallow,
  )

  const navigate = useNavigate()
  const widgetRef = useRef<HTMLDivElement>(null)
  const [showTransferPanel, setShowTransferPanel] = useState(false)
  const [transferNumber, setTransferNumber] = useState('')
  const [showScreenPop, setShowScreenPop] = useState(true)
  const [matchedContactId, setMatchedContactId] = useState<string | null>(null)
  const [callNotes, setCallNotes] = useState('')
  const notesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Reset notes when a new call starts
  useEffect(() => {
    if (callStatus === 'connected') setCallNotes('')
  }, [activeCallSid, callStatus])

  const saveNotes = useMemo(() => (sid: string, text: string) => {
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current)
    notesDebounceRef.current = setTimeout(() => {
      void apiClient.patch(`/api/telephony/calls/${sid}/notes`, { notes: text }).catch(() => undefined)
    }, 500)
  }, [])

  // AC-21: listen for the first user gesture to unlock ringtone autoplay.
  useUserInteracted()

  const isIOS = typeof navigator !== 'undefined'
    && /iP(hone|ad|od)/.test(navigator.userAgent)
    && !(window as unknown as { MSStream?: unknown }).MSStream
  const hasUserInteracted = useUIStore((s) => s.hasUserInteracted)

  // AC-20: clicking the OS notification dispatches 'softphone:focus'
  useEffect(() => {
    const onFocusEvent = () => {
      widgetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    window.addEventListener('softphone:focus', onFocusEvent)
    return () => window.removeEventListener('softphone:focus', onFocusEvent)
  }, [])

  // Only people who can make and take calls ask for a calling token: Crew got
  // a 403 on every page load (UX-010).
  const canCall = usePermission('calls.make')
  const deviceInitRef = useRef(false)
  useEffect(() => {
    if (deviceInitRef.current) return
    if (!canCall) return
    if (isIOS && !hasUserInteracted) return
    deviceInitRef.current = true
    initDevice()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIOS, hasUserInteracted, canCall])

  // iOS one-way-audio fix: mount a hidden <audio autoPlay playsInline>
  // element to document.body and register it with the store. Twilio's SDK
  // creates a detached audio element on iOS Safari (no setSinkId support)
  // whose .play() is rejected by autoplay policy because it's called
  // outside a user-gesture stack. Our element is created in the React
  // tree and primed inside click handlers, so iOS treats it as
  // user-activated and the remote stream plays. See callStore
  // attachRemoteAudioToCall for the full explanation.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const audio = document.createElement('audio')
    audio.autoplay = true
    audio.setAttribute('playsinline', '') // required for iOS Safari
    audio.setAttribute('data-role', 'softphone-remote-audio')
    audio.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;'
    document.body.appendChild(audio)
    useCallStore.getState().setRemoteAudioElement(audio)
    return () => {
      useCallStore.getState().setRemoteAudioElement(null)
      try {
        audio.pause()
        audio.srcObject = null
      } catch {
        /* no-op */
      }
      audio.remove()
    }
  }, [])

  // Every user gesture gets a chance to resume Twilio's shared AudioContext.
  // Chrome + Safari sometimes suspend it mid-session (tab backgrounded, OS
  // media-session interrupt, another tab grabbing media). When that happens
  // silently, outbound calls still signal-connect but remote audio plays
  // to a suspended ctx → user hears nothing. Resuming on any subsequent
  // gesture restores playback without the user noticing.
  useEffect(() => {
    if (!hasUserInteracted) return
    const onGesture = () => { void resumeTwilioAudio() }
    window.addEventListener('pointerdown', onGesture, { passive: true })
    window.addEventListener('keydown', onGesture)
    return () => {
      window.removeEventListener('pointerdown', onGesture)
      window.removeEventListener('keydown', onGesture)
    }
  }, [hasUserInteracted])

  // AC-37: destroy Twilio Device on tab close / component unmount so no
  // call leg is left in-progress server-side. If there's an active call,
  // POST /api/telephony/hangup via fetch + keepalive so the request can
  // carry the CSRF header (sendBeacon cannot set custom headers, and we
  // now require CSRF on user-initiated telephony routes — Theme 2).
  useEffect(() => {
    const beforeUnloadHandler = () => {
      const state = useCallStore.getState()
      const call = state.activeCall
      const callSid = call
        ? (call.parameters as Record<string, string>)?.CallSid
        : undefined

      if (callSid && typeof fetch !== 'undefined') {
        try {
          // The _csrf cookie is host-only on the API origin, so in production
          // (SPA on app.*, API on api.*) document.cookie cannot see it. Echo
          // the in-memory token apiClient already fetched instead.
          const csrf = getCsrfToken()
          void fetch(apiUrl('/api/telephony/hangup'), {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: {
              'Content-Type': 'application/json',
              ...(csrf ? { 'x-csrf-token': csrf } : {}),
            },
            body: JSON.stringify({ callSid }),
          })
        } catch {
          /* best-effort */
        }
      }

      const device = state.device
      try {
        if (call) call.disconnect()
        if (device) device.destroy()
      } catch {
        /* already destroyed */
      }
    }

    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
      useCallStore.getState().destroyDevice()
      // destroyDevice voids any in-flight init, so a remount (StrictMode,
      // or the shell swapping layouts) must init again — otherwise the
      // store never learns whether the business line is connected.
      deviceInitRef.current = false
    }
  }, [])

  useEffect(() => {
    if (callStatus === 'idle') setMatchedContactId(null)
  }, [callStatus])

  // Make sure the CSRF token is cached while a call is live so the
  // synchronous beforeunload hangup above can echo it back.
  useEffect(() => {
    if (callStatus !== 'idle') void warmCsrf()
  }, [callStatus])

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(clearError, 5000)
      return () => clearTimeout(timeout)
    }
  }, [error, clearError])

  const handleMakeCall = useCallback(
    (number: string, meta?: MakeCallMeta) => {
      makeCall(number, meta)
      setDialPadOpen(false)
    },
    [makeCall, setDialPadOpen]
  )

  const isActive = callStatus !== 'idle' && callStatus !== 'ended'
  const showIncoming = callStatus === 'ringing' && callDirection === 'inbound' && !!incomingCall

  // Blocking modals + banners — always mounted at app root
  const blockingModals = (
    <>
      <QuietHoursModal />
      <PhoneOfflineBanner />
      <MicPermissionBanner />
      <PostCallCard />
    </>
  )

  // ─── State 1: Incoming call ──────────────────────────────────────────────────
  if (showIncoming) {
    const handleAccept = () => {
      acceptCall()
      setShowScreenPop(false)
      if (matchedContactId) {
        navigate(`/customers/people/${matchedContactId}`)
      }
    }
    const handleDecline = () => {
      rejectCall()
      setShowScreenPop(false)
    }

    return (
      <>
        {blockingModals}

        {/* Mobile full-screen takeover */}
        <MobileIncomingCallOverlay
          callerInfo={callerInfo}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />

        {/* Desktop corner card */}
        <div className="hidden md:block fixed bottom-6 right-6 z-[55] w-72" ref={widgetRef}>
          {showScreenPop && callerInfo?.number && (
            <div className="mb-2">
              <ScreenPop
                callerNumber={callerInfo.number}
                onContactMatched={(id) => setMatchedContactId(id)}
                onViewContact={(id) => {
                  setShowScreenPop(false)
                  navigate(`/customers/people/${id}`)
                }}
                onCreateContact={(phone, callSessionId) => {
                  setShowScreenPop(false)
                  navigate(`/customers/people?phone=${encodeURIComponent(phone)}`, { state: { callSessionId } })
                }}
                onDismiss={() => setShowScreenPop(false)}
              />
            </div>
          )}
          <IncomingCallToast
            callerInfo={callerInfo}
            onAccept={handleAccept}
            onReject={handleDecline}
          />
        </div>
      </>
    )
  }

  // ─── State 2: Active call ────────────────────────────────────────────────────
  if (isActive) {
    // Derive ring tone from call state (Direction B: avatar carries call state)
    let ringState: CallRingState = 'active'
    if (callStatus === 'connecting') ringState = 'ringing'
    else if (isOnHold) ringState = 'hold'
    else if (connectionQuality === 'poor') ringState = 'poor'

    const callerLabel = callerInfo?.name || callerInfo?.number || 'Unknown'

    return (
      <>
        {blockingModals}
        <div
          className="fixed right-4 left-4 md:right-6 md:left-auto z-[55] md:w-[22rem] animate-slide-up-sheet md:animate-fade-up"
          style={{ bottom: 'var(--softphone-fab-bottom)' }}
          ref={widgetRef}
        >
          <div
            className="ds-tile-solid"
            style={{ borderRadius: 'var(--r-2xl, 40px)', overflow: 'hidden' }}
          >
            {/* ── Status eyebrow ──────────────────────────────────────────── */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px 0',
              }}
            >
              <span
                className="eyebrow"
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
                }}
              >
                {callStatus === 'connecting'
                  ? 'Connecting'
                  : isOnHold
                    ? 'On hold'
                    : callDirection === 'inbound'
                      ? 'On call · Incoming'
                      : 'On call'}
              </span>
              {callStatus === 'connected' && <ConnectionQualityIndicator />}
            </div>

            {/* ── Hero column ─────────────────────────────────────────────── */}
            <div
              className="animate-fade-up"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 18,
                padding: '20px 18px 14px',
                textAlign: 'center',
              }}
            >
              <AvatarStateRing
                name={callerInfo?.name ?? undefined}
                state={ringState}
                size="md"
                pulse={callStatus === 'connecting'}
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <span
                  className="date-hero"
                  style={{
                    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                    lineHeight: 1.05,
                    color: '#FFFFFF',
                    maxWidth: '24ch',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {callerLabel}
                </span>
                {callerInfo?.name && callerInfo.number && (
                  <span
                    style={{
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.55)',
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {callerInfo.number}
                  </span>
                )}
              </div>
              {callStatus === 'connected' ? (
                <CallTimer startTime={callStartTime} variant="hero" />
              ) : (
                <span
                  className="stat-hero"
                  style={{ color: 'var(--midnight-text3)', letterSpacing: '0.04em' }}
                >
                  • • •
                </span>
              )}
            </div>

            {/* ── Control dock ────────────────────────────────────────────── */}
            <div style={{ padding: '0 4px 12px' }}>
              <CallControlDock
                footer={
                  <button
                    type="button"
                    onClick={() => {
                      hangUp()
                      resetTransfer()
                      setShowTransferPanel(false)
                    }}
                    aria-label="Hang up call"
                    className="btn-danger"
                    style={{
                      width: '100%',
                      height: 48,
                      borderRadius: '9999px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: 15,
                      fontWeight: 600,
                    }}
                  >
                    <PhoneOff style={{ width: 18, height: 18 }} />
                    End call
                  </button>
                }
              >
                <CallControlDockButton
                  onClick={toggleMute}
                  pressed={isMuted}
                  tone={isMuted ? 'active' : 'neutral'}
                  label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                  icon={
                    isMuted ? (
                      <MicOff style={{ width: 20, height: 20 }} />
                    ) : (
                      <Mic style={{ width: 20, height: 20 }} />
                    )
                  }
                />
                <CallControlDockButton
                  onClick={() => {
                    if (isOnHold) resumeCall()
                    else holdCall()
                  }}
                  pressed={isOnHold}
                  tone={isOnHold ? 'active' : 'neutral'}
                  label={isOnHold ? 'Resume call' : 'Hold call'}
                  icon={
                    isOnHold ? (
                      <Play style={{ width: 20, height: 20 }} />
                    ) : (
                      <Pause style={{ width: 20, height: 20 }} />
                    )
                  }
                />
                {isSpeakerOn !== null && (
                  <CallControlDockButton
                    onClick={() => {
                      void toggleSpeaker()
                    }}
                    pressed={isSpeakerOn}
                    tone={isSpeakerOn ? 'active' : 'neutral'}
                    label={isSpeakerOn ? 'Switch to earpiece' : 'Switch to speakerphone'}
                    icon={
                      isSpeakerOn ? (
                        <Volume2 style={{ width: 20, height: 20 }} />
                      ) : (
                        <Volume style={{ width: 20, height: 20 }} />
                      )
                    }
                  />
                )}
                <CallControlDockButton
                  onClick={() => setDialPadOpen(!dialPadOpen)}
                  pressed={dialPadOpen}
                  tone={dialPadOpen ? 'active' : 'neutral'}
                  label={dialPadOpen ? 'Close dial pad' : 'Open dial pad'}
                  icon={<Grid3X3 style={{ width: 20, height: 20 }} />}
                />
                {callStatus === 'connected' && (
                  <CallControlDockButton
                    onClick={() => {
                      setShowTransferPanel(!showTransferPanel)
                      setDialPadOpen(false)
                    }}
                    pressed={showTransferPanel || transferStatus !== 'idle'}
                    tone={
                      showTransferPanel || transferStatus !== 'idle' ? 'active' : 'neutral'
                    }
                    label="Transfer call"
                    icon={<ArrowRightLeft style={{ width: 20, height: 20 }} />}
                  />
                )}
              </CallControlDock>
            </div>

            {/* Transfer panel */}
            {showTransferPanel && (
              <>
                <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {transferStatus === 'idle' && (
                    <>
                      <input
                        type="tel"
                        value={transferNumber}
                        onChange={(e) => setTransferNumber(e.target.value)}
                        placeholder="Transfer to..."
                        style={{
                          width: '100%',
                          height: 40,
                          borderRadius: 'var(--r-sm)',
                          border: '0.5px solid var(--midnight-border)',
                          background: 'var(--midnight-surface-2, #14141A)',
                          padding: '0 12px',
                          color: '#FFFFFF',
                          // 16px floor — iOS Safari auto-zooms <16px (FND-M-004).
                          fontSize: 16,
                          boxSizing: 'border-box',
                        }}
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => {
                          if (transferNumber.trim()) {
                            initiateWarmTransfer(transferNumber.trim())
                          }
                        }}
                        disabled={!transferNumber.trim()}
                        style={{ width: '100%', height: 40, fontSize: 13 }}
                      >
                        Warm transfer
                      </button>
                    </>
                  )}

                  {transferStatus === 'connecting' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 'var(--r-pill)',
                          background: 'rgba(255,255,255,0.60)',
                          animation: 'pulse 1s infinite',
                          display: 'inline-block',
                        }}
                      />
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.60)' }}>
                        Connecting to {transferTarget}...
                      </span>
                    </div>
                  )}

                  {transferStatus === 'connected' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 'var(--r-pill)',
                            background: 'var(--midnight-mint)',
                            boxShadow: '0 0 8px rgba(168,230,206,0.5)',
                            display: 'inline-block',
                          }}
                        />
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.60)' }}>
                          Connected — Complete transfer?
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => {
                            completeWarmTransfer()
                            setShowTransferPanel(false)
                            setTransferNumber('')
                          }}
                          style={{ flex: 1, height: 36, fontSize: 12, padding: '0 12px' }}
                        >
                          <Check style={{ width: 12, height: 12 }} />
                          Complete
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            cancelWarmTransfer()
                            setTransferNumber('')
                          }}
                          style={{
                            flex: 1,
                            height: 36,
                            borderRadius: 'var(--r-pill)',
                            border: '0.5px solid var(--midnight-border)',
                            background: 'var(--midnight-surface-2, #14141A)',
                            color: 'rgba(255,255,255,0.60)',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            touchAction: 'manipulation',
                          }}
                        >
                          <X style={{ width: 12, height: 12 }} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {transferStatus === 'completed' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                      <Check style={{ width: 14, height: 14, color: 'var(--midnight-mint)' }} />
                      <span style={{ fontSize: 12, color: 'var(--midnight-mint)' }}>Transfer completed</span>
                    </div>
                  )}

                  {transferStatus === 'cancelled' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                      <X style={{ width: 14, height: 14, color: 'var(--midnight-text3)' }} />
                      <span style={{ fontSize: 12, color: 'var(--midnight-text3)' }}>Transfer cancelled</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* In-call dial pad — AC-17: DTMF mode */}
            {dialPadOpen && (
              <>
                <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.08)' }} />
                <DialPad
                  mode="dtmf"
                  onCall={() => { /* unused in dtmf mode */ }}
                  onDigit={(digit) => {
                    if (activeCall) {
                      activeCall.sendDigits(digit)
                    }
                  }}
                  disabled={!activeCall}
                />
              </>
            )}

            {/* In-call notes — autosave to CallSession on debounce */}
            {callStatus === 'connected' && (
              <>
                <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ padding: '10px 14px' }}>
                  <textarea
                    value={callNotes}
                    onChange={(e) => {
                      setCallNotes(e.target.value)
                      if (activeCallSid) saveNotes(activeCallSid, e.target.value)
                    }}
                    placeholder="Call notes…"
                    rows={3}
                    style={{
                      width: '100%',
                      resize: 'none',
                      background: 'var(--midnight-surface-2, #14141A)',
                      border: '0.5px solid var(--midnight-border)',
                      borderRadius: 'var(--r-sm)',
                      padding: '8px 10px',
                      color: '#FFFFFF',
                      // 16px floor — iOS Safari auto-zooms <16px (FND-M-005).
                      fontSize: 16,
                      lineHeight: 1.4,
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </>
    )
  }

  // ─── State 3: Idle — nothing on screen until the phone is opened ─────────
  // Phones open it from the dock's Phone tab; tablet and desktop from the
  // sidebar's Phone row (UX-111). There is no floating idle button any more:
  // it covered row actions, money columns, toast close buttons and the
  // workflow minimap at the bottom-right of every page.
  return (
    <>
      {blockingModals}

      {/* ── Phone (<768px): full-screen sheet, opened from the dock ─────────── */}
      {softphoneExpanded && (
        <div className="md:hidden">
          <PhoneSheet
            title="Phone"
            onClose={() => setSoftphoneExpanded(false)}
            data-testid="softphone-mobile-sheet"
          >
            <SoftphonePanel onCall={handleMakeCall} />
          </PhoneSheet>
        </div>
      )}

      {/* Phone error toast — shown independently of the panel */}
      {error && (
        <div className="md:hidden fixed right-4 bottom-0 z-[36] w-64 animate-fade-up pointer-events-none">
          {/* extra=200 keeps the toast clear of the expanded mobile panel (96px panel
              + ~88px dock + 16px gap). CLAUDE.md §8. */}
          <SafeAreaBottom extra={200} className="pointer-events-auto">
            <SoftphoneError error={error} onDismiss={clearError} />
          </SafeAreaBottom>
        </div>
      )}

      {/* ── Tablet / desktop (≥768px): panel at the bottom-right, opened from
          the sidebar. Only takes space while it is open or has an error. ── */}
      {(softphoneExpanded || error) && (
        <div
          className="hidden md:flex flex-col items-end gap-2 fixed right-6 z-[35]"
          style={{ bottom: 'var(--softphone-fab-bottom)' }}
          ref={widgetRef}
          data-testid="softphone-desktop-layer"
        >
          {error && (
            <div className="w-64 animate-fade-up">
              <SoftphoneError error={error} onDismiss={clearError} />
            </div>
          )}
          {/* Fixed height keeps Dialer/Contacts/Recents visually identical
              instead of jumping between tab heights. */}
          {softphoneExpanded && (
            <div
              className="w-[22rem] animate-popover-in"
              // Sized to the window, not a fixed 540px that the keypad
              // overflowed. The body scrolls if a window is shorter still,
              // so the Call button is never cut off (UX-069).
              style={{ height: 'min(600px, calc(100dvh - 120px))', display: 'flex' }}
            >
              <PhoneSheet
                variant="panel"
                title="Phone"
                onClose={() => setSoftphoneExpanded(false)}
              >
                <SoftphonePanel onCall={handleMakeCall} density="compact" />
              </PhoneSheet>
            </div>
          )}
        </div>
      )}
    </>
  )
}

function SoftphoneError({ error, onDismiss }: { error: string; onDismiss: () => void }) {
  return (
    <div className="ds-tile" role="alert" style={{ borderColor: 'rgba(244,181,189,0.35)', padding: '12px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <p style={{ fontSize: 12, color: 'var(--midnight-blush)', flex: 1 }}>{error}</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="tap-target"
          style={{
            color: 'rgba(255,255,255,0.62)',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
        >
          <X style={{ width: 12, height: 12 }} />
        </button>
      </div>
    </div>
  )
}
